import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { BRAND } from "@/data/content";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/success-stories", label: "Success Stories" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-gl-bg/85 backdrop-blur-xl border-b border-gl-gold/15 py-4"
          : "bg-transparent py-6"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
        <Link
          to="/"
          data-testid="logo-link"
          className="flex flex-col leading-none group"
        >
          <span className="font-serif-display text-2xl md:text-[26px] text-gl-text tracking-wide">
            GoodLiving
          </span>
          <span className="text-[10px] md:text-xs uppercase tracking-[0.45em] text-gl-gold mt-1">
            Strategy
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-10">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-sm tracking-wide transition-colors duration-300 hover:text-gl-gold ${
                  isActive ? "text-gl-gold" : "text-gl-text2"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/contact"
            data-testid="nav-book-consultation"
            className="px-7 py-3 bg-gl-gold text-gl-bg text-sm font-medium tracking-wide hover:bg-gl-goldHover transition-colors duration-300"
          >
            Book Consultation
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden text-gl-text"
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </nav>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-gl-bg/95 backdrop-blur-xl border-t border-gl-gold/15 mt-4"
        >
          <div className="flex flex-col px-6 py-6 gap-5">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                className={({ isActive }) =>
                  `text-base tracking-wide ${
                    isActive ? "text-gl-gold" : "text-gl-text2"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="mobile-book-consultation"
              className="mt-2 px-6 py-3 bg-gl-gold text-gl-bg text-center text-sm font-medium tracking-wide"
            >
              Book Consultation
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
